import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { BarChart3, TrendingDown, Users, Activity, MapPin, Globe2, Loader2 } from "lucide-react";

const RANGES = [
  { d: 7, label: "7 days" },
  { d: 30, label: "30 days" },
  { d: 90, label: "90 days" },
];

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr("");
    axios.get(`${API}/admin/analytics`, { params: { days } })
      .then(r => { if (alive) setData(r.data); })
      .catch(e => { if (alive) setErr(e?.response?.data?.detail || "Failed to load analytics"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  if (loading) return <div className="p-10 flex items-center gap-2 text-ink-soft" data-testid="analytics-loading"><Loader2 className="w-4 h-4 animate-spin"/> Loading analytics…</div>;
  if (err) return <div className="p-8 text-red-600 font-bold" data-testid="analytics-error">{err}</div>;
  if (!data) return null;

  const topFunnel = data.funnel?.[0]?.count || 1;

  return (
    <div className="space-y-8" data-testid="admin-analytics-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold inline-flex items-center gap-2"><BarChart3 className="w-7 h-7 text-brand"/> Product Analytics</h1>
          <p className="text-sm text-ink-soft mt-1">Funnel, traffic sources & activity — self-hosted, no third-party tracker.</p>
        </div>
        <div className="inline-flex bg-surface border border-line rounded-full p-1" data-testid="analytics-range-tabs">
          {RANGES.map(r => (
            <button key={r.d} data-testid={`range-${r.d}`} onClick={() => setDays(r.d)}
              className={`px-4 py-1.5 text-sm font-bold rounded-full transition-colors ${days === r.d ? "bg-ink text-white" : "text-ink-soft hover:text-ink"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </header>

      {/* KPI cards */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="analytics-kpis">
        <KpiCard testid="kpi-events" label="Total events" value={data.totals.events} icon={<Activity className="w-4 h-4"/>}/>
        <KpiCard testid="kpi-signups" label="New signups" value={data.totals.signups_in_window} icon={<Users className="w-4 h-4"/>}/>
        <KpiCard testid="kpi-dau" label="DAU (24h)" value={data.totals.dau} icon={<Users className="w-4 h-4"/>}/>
        <KpiCard testid="kpi-mau" label="MAU (30d)" value={data.totals.mau} icon={<Users className="w-4 h-4"/>}/>
        <KpiCard testid="kpi-unique-events" label="Unique event types" value={data.totals.unique_events} icon={<BarChart3 className="w-4 h-4"/>}/>
      </section>

      {/* Funnel */}
      <section className="bg-surface border border-line rounded-3xl p-6 shadow-soft" data-testid="analytics-funnel">
        <div className="flex items-center gap-2 mb-4">
          <TrendingDown className="w-5 h-5 text-brand"/>
          <h2 className="font-heading text-xl font-extrabold">Signup → Activation Funnel</h2>
        </div>
        <div className="space-y-3">
          {data.funnel.map((s, i) => {
            const pct = topFunnel ? Math.round((s.count / topFunnel) * 100) : 0;
            const dropoff = i > 0 ? (data.funnel[i-1].count - s.count) : 0;
            const dropPct = i > 0 && data.funnel[i-1].count ? Math.round((dropoff / data.funnel[i-1].count) * 100) : 0;
            return (
              <div key={s.key} data-testid={`funnel-${s.key}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-bold">{i+1}. {s.label}</span>
                  <span className="text-ink-soft">
                    <span className="font-bold text-ink">{s.count.toLocaleString()}</span>
                    <span className="ml-2">({pct}% of top)</span>
                    {i > 0 && dropoff > 0 && <span className="ml-2 text-red-600">↓ {dropPct}% drop-off</span>}
                  </span>
                </div>
                <div className="h-3 bg-surface-muted rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-brand to-brand-hover transition-all" style={{width: `${Math.max(pct, 2)}%`}}/>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-ink-soft mt-4 italic">Tip: Highest drop-off = your biggest ROI opportunity. Ship a fix, watch this chart move.</p>
      </section>

      {/* Two-column: Traffic sources + Top pages */}
      <section className="grid lg:grid-cols-2 gap-6">
        <div className="bg-surface border border-line rounded-3xl p-6 shadow-soft" data-testid="analytics-traffic">
          <div className="flex items-center gap-2 mb-4">
            <Globe2 className="w-5 h-5 text-brand"/>
            <h2 className="font-heading text-xl font-extrabold">Traffic sources</h2>
          </div>
          <div className="text-xs font-bold text-ink-soft uppercase tracking-wider mb-2">UTM campaigns</div>
          {data.utm_sources.length === 0 ? (
            <div className="text-sm text-ink-soft italic mb-4">No UTM-tagged traffic yet. Tag your ad links like <code className="bg-surface-muted px-1.5 py-0.5 rounded text-[11px]">?utm_source=facebook&utm_campaign=diwali</code></div>
          ) : (
            <ul className="space-y-1.5 mb-4">
              {data.utm_sources.map((u, i) => (
                <li key={i} className="flex items-center justify-between text-sm bg-surface-muted rounded-lg px-3 py-2">
                  <span><b>{u.source}</b>{u.medium && <span className="text-ink-soft"> · {u.medium}</span>}{u.campaign && <span className="text-ink-soft"> · {u.campaign}</span>}</span>
                  <span className="font-bold text-ink">{u.count}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="text-xs font-bold text-ink-soft uppercase tracking-wider mb-2">Top referrers</div>
          {data.referrers.length === 0 ? (
            <div className="text-sm text-ink-soft italic">No external referrers yet.</div>
          ) : (
            <ul className="space-y-1.5">
              {data.referrers.map((r, i) => (
                <li key={i} className="flex items-center justify-between text-sm bg-surface-muted rounded-lg px-3 py-2">
                  <span className="truncate max-w-[70%]" title={r.referrer}>{r.referrer}</span>
                  <span className="font-bold text-ink">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-surface border border-line rounded-3xl p-6 shadow-soft" data-testid="analytics-top-pages">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-brand"/>
            <h2 className="font-heading text-xl font-extrabold">Top pages</h2>
          </div>
          {data.top_pages.length === 0 ? (
            <div className="text-sm text-ink-soft italic">No page views yet — data appears within minutes of first visit.</div>
          ) : (
            <ul className="space-y-1.5">
              {data.top_pages.map((p, i) => (
                <li key={i} className="flex items-center justify-between text-sm bg-surface-muted rounded-lg px-3 py-2">
                  <span className="font-mono text-xs truncate max-w-[70%]" title={p.page}>{p.page}</span>
                  <span className="font-bold text-ink">{p.views}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Event log */}
      <section className="bg-surface border border-line rounded-3xl p-6 shadow-soft" data-testid="analytics-events">
        <h2 className="font-heading text-xl font-extrabold mb-4">All events (top 25)</h2>
        {data.events_by_name.length === 0 ? (
          <div className="text-sm text-ink-soft italic">No events tracked yet in this window.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-2">
            {data.events_by_name.map((e, i) => (
              <div key={i} className="flex items-center justify-between text-sm bg-surface-muted rounded-lg px-3 py-2">
                <span className="font-mono text-[12px]">{e.event}</span>
                <span className="font-bold">{e.count}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function KpiCard({ label, value, icon, testid }) {
  return (
    <div data-testid={testid} className="bg-surface border border-line rounded-2xl p-4 shadow-soft">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-ink-soft">{icon} {label}</div>
      <div className="font-heading text-3xl font-extrabold mt-1.5">{(value ?? 0).toLocaleString()}</div>
    </div>
  );
}
